import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import SimpleCandidateCard from "../../components/SimpleCandidateCard";
import VerifiedBadge from "../../components/VerifiedBadge";
import CandidateSearchFilters from "../../components/CandidateSearchFilters";
import ContactModal from "../../components/ContactModal";
import {
  CandidateSearchRequest,
  CandidateSearchResponse,
  CandidateListItem,
  CandidateSearchFilters as CandidateSearchFiltersType,
  Candidate,
} from "../../types";
import apiService from "../../services/api";
import Toast from "../../components/Toast";
import { getTalentMarketPlaceSendMessage } from "./Candidate/api/Candidate.api";
import TablePagination from "../company/JobManagement/Components/TablePagination";

type SearchScope = "company" | "marketplace"; 

interface SearchTab {
  id: SearchScope;
  name: string;
  description: string;
  icon: string;
  requiresPro: boolean;
}

const DEFAULT_CANDIDATES_PER_PAGE = 8;

const getRequestPageSize = (request?: CandidateSearchRequest | null) =>
  request?.page_size ?? DEFAULT_CANDIDATES_PER_PAGE;

const toWorkTypeFilter = (filters: CandidateSearchFiltersType) => {
  const remoteWorkTypeMap: Record<string, string> = {
    remote_only: "remote",
    on_site: "onsite",
  };

  if (filters.remote_preference) {
    return (
      remoteWorkTypeMap[filters.remote_preference] ??
      filters.remote_preference
    );
  }

  return filters.employment_type || undefined;
};

const CandidateSearch: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<SearchScope>(
    (location.state?.activeTab as SearchScope) ?? "company",
  );
  const [viewMode, setViewMode] = useState<"list" | "grid">("grid");
  const initialCompanySearchRequest: CandidateSearchRequest =
    location.state?.companySearchRequest ?? {
      query: "",
      skills: [],
      page: 1,
      page_size: DEFAULT_CANDIDATES_PER_PAGE,
      sort_by: "created_at",
      sort_order: "desc",
    };
  const initialMarketplaceSearchRequest: CandidateSearchRequest =
    location.state?.marketplaceSearchRequest ?? {
      query: "",
      skills: [],
      page: 1,
      page_size: DEFAULT_CANDIDATES_PER_PAGE,
      sort_by: "created_at",
      sort_order: "desc",
    };

  // Separate state for each tab to prevent data mixing
  const [companyData, setCompanyData] = useState<{
    candidates: CandidateListItem[];
    searchResponse: CandidateSearchResponse | null;
    searchRequest: CandidateSearchRequest;
  }>({
    candidates: [],
    searchResponse: null,
    searchRequest: initialCompanySearchRequest,
  });

  const [marketplaceData, setMarketplaceData] = useState<{
    candidates: CandidateListItem[];
    searchResponse: CandidateSearchResponse | null;
    searchRequest: CandidateSearchRequest;
  }>({
    candidates: [],
    searchResponse: null,
    searchRequest: initialMarketplaceSearchRequest,
  });
  const [candidatePageSizes, setCandidatePageSizes] = useState<
    Record<SearchScope, number>
  >({
    company: getRequestPageSize(initialCompanySearchRequest),
    marketplace: getRequestPageSize(initialMarketplaceSearchRequest),
  });

  const [loading, setLoading] = useState(false);

  // Get current data based on active tab
  const currentData = activeTab === "company" ? companyData : marketplaceData;
  const candidates = currentData.candidates;
  const searchResponse = currentData.searchResponse;
  const searchRequest = currentData.searchRequest;
  const candidatesPerPage = candidatePageSizes[activeTab];

  // Add filters state for CandidateSearchFilters component
  const [filters, setFilters] = useState<CandidateSearchFiltersType>({
    search: "",
    skills: "",
    location: "",
    job_title: "",
    experience_min: undefined,
    experience_max: undefined,
    salary_min: undefined,
    salary_max: undefined,
    education_level: "",
    employment_type: "",
    remote_preference: "",
    availability: "",
    languages: [],
    company: "",
    is_identity_verified: false,
  });

  // Contact modal state
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(
    null,
  );
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "warning" | "info";
    isVisible: boolean;
  }>({
    message: "",
    type: "success",
    isVisible: false,
  });

  // Define search tabs with access control
  const searchTabs: SearchTab[] = [
    {
      id: "company",
      name: "Company Database",
      description: "Your uploaded resumes & candidates",
      icon: "🏢",
      requiresPro: false,
    },
    {
      id: "marketplace",
      name: "Talent Marketplace",
      description: "Active candidate profiles seeking opportunities",
      icon: "🌟",
      requiresPro: true,
    },
  ];

  // Check if user has access to marketplace
  const hasMarketplaceAccess = () => {
    // First check if company has pricing plan with features
    return true;
    // if (
    //   user?.company?.pricing_plan?.features &&
    //   Array.isArray(user.company.pricing_plan.features)
    // ) {
    //   // Check if marketplace feature is enabled in pricing plan
    //   const features = user.company.pricing_plan.features;
    //   const hasMarketplaceFeature = features.some(
    //     (feature: string) =>
    //       feature.toLowerCase().includes("marketplace") ||
    //       feature.toLowerCase().includes("talent marketplace") ||
    //       feature.toLowerCase().includes("advanced_search") ||
    //                 feature.toLowerCase().includes("advanced_search"),

    //   );
    //   if (hasMarketplaceFeature) return true;
    // }

    // // Also check the feature flags directly (for database-driven feature flags)
    // const pricingPlan = user?.company?.pricing_plan;
    // if (pricingPlan?.has_job_seeker_access) {
    //   return true;
    // }

    // // Check pricing plan price as secondary indicator (paid plans typically have marketplace access)
    // if (pricingPlan && typeof pricingPlan.price_monthly === "number") {
    //   return pricingPlan.price_monthly > 0; // Paid plans have marketplace access
    // }

    // // No pricing plan or features defined - default to no access
    // return false;
  };

  useEffect(() => {
    navigate(location.pathname, {
      replace: true,
      state: {
        activeTab,
        companySearchRequest: companyData.searchRequest,
        marketplaceSearchRequest: marketplaceData.searchRequest,
      },
    });
  }, [
    activeTab,
    companyData.searchRequest,
    marketplaceData.searchRequest,
    location.pathname,
    navigate,
  ]);

  // Handle tab change with access control
  const handleTabChange = (tabId: SearchScope) => {
    if (tabId === "marketplace" && !hasMarketplaceAccess()) {
      // Show upgrade modal or redirect to pricing
      return;
    }
    setActiveTab(tabId);

    // Perform search for the new tab if no data exists
    const targetData = tabId === "company" ? companyData : marketplaceData;
    if (targetData.candidates.length === 0 && !targetData.searchResponse) {
      performSearch(targetData.searchRequest, tabId);
    }
  };

  const performSearch = async (
    request: CandidateSearchRequest,
    searchScope?: SearchScope,
  ) => {
    try {
      setLoading(true);

      // Use provided scope or current active tab
      const scope = searchScope || activeTab;
      const requestPageSize =
        request.page_size ??
        candidatePageSizes[scope] ??
        DEFAULT_CANDIDATES_PER_PAGE;
      const normalizedRequest: CandidateSearchRequest = {
        ...request,
        page_size: requestPageSize,
      };

      setCandidatePageSizes((prev) => ({
        ...prev,
        [scope]: requestPageSize,
      }));

      // Add search scope to request
      const searchParams: CandidateSearchRequest = {
        ...normalizedRequest,
        search_scope: scope, // 'company' or 'marketplace'
      };

      const response = await apiService.searchCandidates(searchParams);

      // Update the correct tab's data
      if (scope === "company") {
        setCompanyData({
          candidates: response.candidates,
          searchResponse: response,
          searchRequest: normalizedRequest,
        });
      } else {
        setMarketplaceData({
          candidates: response.candidates,
          searchResponse: response,
          searchRequest: normalizedRequest,
        });
      }

      const candidateIds = response.candidates
        .map((candidate) => candidate.id)
        .filter(Boolean);

      // Track marketplace candidate appearances for candidate analytics.
      if (
        searchParams.search_scope === "marketplace" &&
        candidateIds.length > 0
      ) {
        try {
          if (process.env.NODE_ENV === "development") {
            console.info(
              "Tracking marketplace search appearances",
              candidateIds,
            );
          }

          await apiService.trackSearchAppearance(candidateIds, {
            query: searchParams.query || "",
            skills: searchParams.skills || [],
            location: searchParams.location || "",
            experience_min: searchParams.experience_min,
            experience_max: searchParams.experience_max,
            job_title: searchParams.job_title || "",
            company: searchParams.company || "",
            salary_min: searchParams.salary_min,
            salary_max: searchParams.salary_max,
            ai_score_min: searchParams.ai_score_min,
          });
        } catch (analyticsError) {
          // Don't fail the search if analytics tracking fails
          console.warn("Analytics tracking failed:", analyticsError);
        }
      }
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handler for CandidateSearchFilters component
  const handleFiltersChange = (newFilters: CandidateSearchFiltersType) => {
    setFilters(newFilters);
  };
  const handleSearch = (overrideFilters?: CandidateSearchFiltersType) => {
    const appliedFilters = overrideFilters || filters;
    const languageList = appliedFilters.languages
      ?.map((language) => language.trim().toLowerCase())
      .filter(Boolean)
      .join(",");

    const searchRequest: CandidateSearchRequest = {
      query: appliedFilters.search || "",
      skills: appliedFilters.skills
        ? appliedFilters.skills
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
        : [],
      location: appliedFilters.location || "",
      experience_min: appliedFilters.experience_min,
      experience_max: appliedFilters.experience_max,
      salary_min: appliedFilters.salary_min,
      salary_max: appliedFilters.salary_max,
      job_title: appliedFilters.job_title || "",
      company: appliedFilters.company || "",
      education_level: appliedFilters.education_level || undefined,
      availability: appliedFilters.availability || undefined,
      work_type: toWorkTypeFilter(appliedFilters),
      language_list: languageList || undefined,
      is_identity_verified: appliedFilters.is_identity_verified || undefined,
      page: 1,
      page_size: candidatesPerPage,
      sort_by: "created_at",
      sort_order: "desc",
      track_search: true,
    };

    performSearch(searchRequest);
  };

  // Initial load — fetch data for the active tab on mount, and also pre-fetch the other tab if needed
  useEffect(() => {
    const initialTab = (location.state?.activeTab as SearchScope) ?? "company";

    // Always load the active tab first
    if (initialTab === "marketplace") {
      if (
        marketplaceData.candidates.length === 0 &&
        !marketplaceData.searchResponse
      ) {
        performSearch(marketplaceData.searchRequest, "marketplace");
      }
      // Also load company in background so switching is instant
      if (companyData.candidates.length === 0 && !companyData.searchResponse) {
        performSearch(companyData.searchRequest, "company");
      }
    } else {
      if (companyData.candidates.length === 0 && !companyData.searchResponse) {
        performSearch(companyData.searchRequest, "company");
      }
    }
  }, []);

  // const handleViewProfile = async (candidate: CandidateListItem) => {
  //   try {
  //     // await apiService.trackCandidateProfileView(candidate.id);
  //   } catch (error) {
  //     console.warn("Profile view tracking failed:", error);
  //   }

  //   navigate(`/company/candidate/${candidate.id}`);
  // };
  const handleViewProfile = async (candidate: CandidateListItem) => {
    if (activeTab === "marketplace") {
      try {
        await apiService.trackCandidateProfileView(candidate.id);
      } catch (error) {
        console.warn("Profile view tracking failed:", error);
      }
    }

    navigate(`/company/candidate/${candidate.id}`, {
      state: {
        candidate:
          activeTab === "marketplace"
            ? convertToCandidate(candidate)
            : candidate,
        source: activeTab,
      },
    });
  };
  // const handleViewProfile = (candidate: CandidateListItem) => {
  //   navigate(`/company/candidate/${candidate.id}`, {
  //     state: { candidate, source: activeTab },
  //   });
  //   navigate(`/company/candidate/${candidate.id}`);
  // };
  const handleContact = (candidate: CandidateListItem) => {
    // Convert CandidateListItem to Candidate and open contact modal
    const fullCandidate = convertToCandidate(candidate);
    setSelectedCandidate(fullCandidate);
    setContactModalOpen(true);
  };

  const handleSendMessage = async (message: string, subject: string) => {
    if (!selectedCandidate) return;
    try {
      if (activeTab === "marketplace") {
        await getTalentMarketPlaceSendMessage(selectedCandidate.id, {
          subject,
          message,
          access_type: "marketplace",
        });
      } else {
        await apiService.contactCandidate(selectedCandidate.id, {
          subject,
          message,
        });
      }
      setToast({
        message: `🎉 Message sent successfully to ${selectedCandidate.first_name} ${selectedCandidate.last_name}!`,
        type: "success",
        isVisible: true,
      });
      setContactModalOpen(false);
      setSelectedCandidate(null);
    } catch (error) {
      console.error("Failed to send message:", error);
      setToast({
        message: "Failed to send message. Please try again.",
        type: "error",
        isVisible: true,
      });
    }
  };
  const handleCloseContactModal = () => {
    setContactModalOpen(false);
    setSelectedCandidate(null);
  };

  // Convert CandidateListItem to Candidate for the card component
  const convertToCandidate = (item: CandidateListItem): Candidate => {
    return {
      ...item,
      company_id: user?.company_id || "",
      updated_at: item.created_at,
      is_active: true,
      full_name: `${item.first_name} ${item.last_name}`,
      current_job_title: item.current_job_title,
      current_company: item.current_company,
      years_of_experience: item.years_of_experience,
      location: item.location,
      ai_match_score: item.ai_match_score,
      ai_career_level: item.ai_career_level,
      is_identity_verified: item.is_identity_verified,
      is_id_verified: item.is_id_verified,
      // Convert simplified skills to full CandidateSkill format
      skills: item.skills?.map((skill: any, index) => ({
        id: `temp-${item.id}-${index}`,
        candidate_id: item.id,
        skill_name: skill.skill_name,
        proficiency_level: skill.proficiency_level || "intermediate",
        years_of_experience: skill.years_of_experience ?? undefined,
        confidence_score: skill.confidence_score ?? undefined,
        context: skill.context ?? undefined,
      })),
    };
  };

  const closeToast = () => {
    setToast((prev) => ({ ...prev, isVisible: false }));
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Candidate Search</h1>
        <p className="mt-2 text-gray-600">
          Find the perfect candidates for your open positions
        </p>
      </div>

      {/* Search Scope Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {searchTabs.map((tab) => {
              const isActive = activeTab === tab.id;
              const hasAccess = !tab.requiresPro || hasMarketplaceAccess();

              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  // disabled={!hasAccess}
                  className={`group relative py-4 px-1 border-b-2 font-medium text-sm ${isActive
                      ? "border-indigo-500 text-indigo-600"
                      : hasAccess
                        ? "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        : "border-transparent text-gray-400 cursor-not-allowed"
                    }`}
                >
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">{tab.icon}</span>
                    <div className="text-left">
                      <div className="flex items-center space-x-2">
                        <span>{tab.name}</span>
                        {tab.requiresPro && (
                          <span className="px-1.5 py-0.5 text-xs font-medium bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-full">
                            PRO
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 font-normal">
                        {tab.description}
                      </div>
                    </div>
                  </div>

                  {/* Pro Feature Tooltip */}
                  {tab.requiresPro && !hasAccess && (
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                      Upgrade to Professional to access Talent Marketplace
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                    </div>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Upgrade Banner for Marketplace */}
      {activeTab === "marketplace" && !hasMarketplaceAccess() && (
        <div className="mb-6 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold mb-2">
                🚀 Unlock Talent Marketplace
              </h3>
              <p className="text-indigo-100 mb-4">
                Access thousands of active candidates seeking new opportunities.
                Expand your talent pool beyond your company database.
              </p>
              <div className="flex items-center space-x-4 text-sm">
                <div className="flex items-center">
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Active job seekers
                </div>
                <div className="flex items-center">
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Fresh talent pool
                </div>
                <div className="flex items-center">
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Advanced matching
                </div>
              </div>
            </div>
            <div className="text-right">
              <button className="bg-white text-indigo-600 px-6 py-3 rounded-lg font-semibold hover:bg-indigo-50 transition-colors">
                Upgrade Now
              </button>
              <p className="text-xs text-indigo-200 mt-2">
                Starting at $99/month
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Search Results Summary */}
      {searchResponse && (
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h2 className="text-lg font-medium text-gray-900">
              {searchResponse.total_count} candidates found
            </h2>
            <span className="text-sm text-gray-500">
              in{" "}
              {activeTab === "company"
                ? "Company Database"
                : "Talent Marketplace"}
            </span>
          </div>

          {/* Search Scope Indicator */}
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <span className="text-lg">
              {searchTabs.find((tab) => tab.id === activeTab)?.icon}
            </span>
            <span>{searchTabs.find((tab) => tab.id === activeTab)?.name}</span>
          </div>
        </div>
      )}

      {/* Search Filters */}
      <div className="mb-6">
        <CandidateSearchFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onSearch={handleSearch}
          isLoading={loading}
        />
      </div>

      {/* View Mode Toggle and Results Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <p className="text-sm text-gray-700">
            {searchResponse ? (
              <>
                Showing{" "}
                {(searchResponse.page - 1) * searchResponse.page_size + 1}-
                {Math.min(
                  searchResponse.page * searchResponse.page_size,
                  searchResponse.total_count,
                )}{" "}
                of{" "}
                <span className="font-medium">
                  {searchResponse.total_count}
                </span>{" "}
                candidates
              </>
            ) : (
              "Search for candidates"
            )}
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">View:</span>
          <div className="flex rounded-lg border border-gray-300">
            <button
              onClick={() => setViewMode("list")}
              className={`px-3 py-1 text-sm font-medium rounded-l-lg ${viewMode === "list"
                  ? "bg-indigo-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 10h16M4 14h16M4 18h16"
                />
              </svg>
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`px-3 py-1 text-sm font-medium rounded-r-lg ${viewMode === "grid"
                  ? "bg-indigo-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      {viewMode === "list" ? (
        <div className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : candidates.length > 0 ? (
            candidates.map((candidate) => (
              <SimpleCandidateCard
                key={candidate.id}
                candidate={convertToCandidate(candidate)}
                onViewProfile={(c) => handleViewProfile(candidate)}
                onContact={(c) => handleContact(candidate)}
              />
            ))
          ) : (
            <div className="text-center py-12">
              <div className="text-4xl mb-4">
                {activeTab === "company" ? "📁" : "🔍"}
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {activeTab === "company"
                  ? "No candidates in your database"
                  : "No candidates found in marketplace"}
              </h3>
              <p className="text-gray-500 mb-4">
                {activeTab === "company"
                  ? "Upload resumes to build your candidate database"
                  : "Try adjusting your search criteria"}
              </p>
              {activeTab === "company" && (
                <button
                  onClick={() => navigate("/company/upload")}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
                >
                  Upload Resumes
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {loading ? (
            Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-sm border p-4 animate-pulse"
              >
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
              </div>
            ))
          ) : candidates.length > 0 ? (
            candidates.map((candidate) => {
              const isVerified =
                candidate.is_identity_verified === true ||
                candidate.is_id_verified === true;

              return (
                <div
                  key={candidate.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                      <span className="text-indigo-600 font-medium text-sm">
                        {candidate.first_name?.[0]}
                        {candidate.last_name?.[0]}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <h3 className="min-w-0 truncate text-sm font-medium text-gray-900">
                          {candidate.first_name} {candidate.last_name}
                        </h3>
                        {isVerified && <VerifiedBadge compact />}
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {candidate.current_job_title || "No title"}
                      </p>
                    </div>
                    {/* {candidate.ai_match_score && (
                    <div className="text-xs font-medium text-green-600">
                      {Math.round(candidate.ai_match_score * 100)}%
                    </div>
                  )} */}
                  </div>

                  <div className="space-y-2 mb-3">
                    {candidate.current_company && (
                      <p className="text-xs text-gray-600 truncate">
                        📍 {candidate.current_company}
                      </p>
                    )}
                    {candidate.location && (
                      <p className="text-xs text-gray-600 truncate">
                        🌍 {candidate.location}
                      </p>
                    )}
                    {Number(candidate.years_of_experience) > 0 && (
                      <p className="text-xs text-gray-600">
                        💼 {candidate.years_of_experience} years exp.
                      </p>
                    )}
                  </div>

                  {candidate.skills && candidate.skills.length > 0 && (
                    <div className="mb-3">
                      <div className="flex flex-wrap gap-1">
                        {candidate.skills.slice(0, 3).map((skill, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {skill.skill_name}
                          </span>
                        ))}
                        {candidate.skills.length > 3 && (
                          <span className="text-xs text-gray-500">
                            +{candidate.skills.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  <div
                    className={`flex mt-2 ${candidate.contact_unlocked ? "space-x-2" : "justify-end"
                      }`}
                  >
                    {candidate.contact_unlocked && (
                      <button
                        onClick={() => handleContact(candidate)}
                        className="flex-1 text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded hover:bg-gray-200"
                      >
                        Contact
                      </button>
                    )}

                    <button
                      onClick={() => handleViewProfile(candidate)}
                      className={`text-xs bg-indigo-600 text-white px-2 py-1 rounded hover:bg-indigo-700
      ${candidate.contact_unlocked ? "flex-1" : ""}
    `}
                    >
                      View
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="col-span-full text-center py-12">
              <div className="text-4xl mb-4">
                {activeTab === "company" ? "📁" : "🔍"}
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {activeTab === "company"
                  ? "No candidates in your database"
                  : "No candidates found in marketplace"}
              </h3>
              <p className="text-gray-500 mb-4">
                {activeTab === "company"
                  ? "Upload resumes to build your candidate database"
                  : "Try adjusting your search criteria"}
              </p>
              {activeTab === "company" && (
                <button
                  onClick={() => navigate("/company/upload")}
                  className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
                >
                  Upload Resumes
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {searchResponse && searchResponse.total_count > 0 && (
        <div className="mt-6">
          <TablePagination
            currentPage={searchResponse.page}
            totalCount={searchResponse.total_count}
            rowsPerPage={searchResponse.page_size}
            onPageChange={(page) => {
              performSearch({
                ...searchRequest,
                page,
                page_size: candidatesPerPage,
              });
            }}
            onRowsPerPageChange={(rows) => {
              setCandidatePageSizes((prev) => ({
                ...prev,
                [activeTab]: rows,
              }));

              performSearch({
                ...searchRequest,
                page: 1,
                page_size: rows,
              });
            }}
            isGrid={viewMode === "grid"}
          />
        </div>
      )}

      {/* Contact Modal */}
      {selectedCandidate && (
        <ContactModal
          candidate={selectedCandidate}
          isOpen={contactModalOpen}
          onClose={handleCloseContactModal}
          onSend={handleSendMessage}
        />
      )}

      {/* Toast Notification */}
      {toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={closeToast}
        />
      )}
    </div>
  );
};

export default CandidateSearch;
